import React, { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '../cn.js';

/**
 * Design-system single select.
 * options: [{ value, label }]
 */
const Select = ({
    options = [],
    value,
    onChange,
    placeholder = '',
    fullWidth = false,
    disabled = false,
    className = '',
}) => {
    const wrapperRef = useRef(null);
    const [open, setOpen] = useState(false);
    const selected = options.find((opt) => String(opt.value) === String(value));

    const handleClickOutside = useCallback((event) => {
        if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
            setOpen(false);
        }
    }, []);

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [handleClickOutside]);

    const pick = (opt) => {
        onChange?.(opt.value, opt);
        setOpen(false);
    };

    return (
        <div
            ref={wrapperRef}
            className={cn(
                'select__choice relative mt-12 box-border flex min-h-[46px] w-full cursor-pointer items-center rounded-input border border-solid border-light-grey bg-white px-16 py-12 text-dark-green',
                fullWidth ? 'select__choice_full-width max-w-none' : 'max-w-[376px]',
                disabled && 'select__choice_disabled cursor-default bg-disabled',
                className,
            )}
            onClick={() => {
                if (!disabled) {
                    setOpen((prev) => !prev);
                }
            }}
            role="listbox"
            tabIndex={disabled ? -1 : 0}
        >
            <span
                className={cn(
                    'select__choice-value min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap',
                    !selected && 'select__choice-placeholder text-secondary-light-grey',
                )}
            >
                {selected ? selected.label : placeholder}
            </span>
            <span
                className={cn(
                    'select__choice-icon ml-auto h-20 w-20 shrink-0 text-dark-green',
                    open ? 'select__choice-icon_active rotate-0' : 'rotate-180',
                )}
                aria-hidden
            >
                ▾
            </span>
            {open && !disabled ? (
                <div className="select__choice-dropdown absolute top-full left-0 z-20 mt-8 max-h-[200px] w-full overflow-y-auto rounded-input border border-solid border-light-grey bg-white">
                    {options.map((opt) => (
                        <div
                            key={String(opt.value)}
                            className={cn(
                                'select__choice-option cursor-pointer px-16 py-12 font-montserrat',
                                String(opt.value) === String(value) && 'select__choice-option_active text-list-hover',
                                'hover:text-list-hover',
                            )}
                            onClick={(e) => {
                                e.stopPropagation();
                                pick(opt);
                            }}
                            role="option"
                            aria-selected={String(opt.value) === String(value)}
                        >
                            {opt.label}
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

export default Select;
